import { isAdvisor, isAxos, isIndia } from '@oneview/utils/branchUtils';
import React from 'react';
import OnboardingSvg from '../assets/svgs/onboarding.svg';

const OnboardingIcon = () => <img src={OnboardingSvg} alt="Onboarding" style={{ width: 20, height: 16 }} />;
import { AdvisorMenuId, ClientMenuId } from './ids';
import { AdvisorMenuRoute, ClientMenuRoute } from './routes';

import {
    Home,
    Users,
    Library,
    TrendingUp,
    ArrowRightLeft,
    Lightbulb,
    FileText,
    HelpCircle,
    Wrench,
    Settings,
    LayoutDashboard,
    Scale,
    Briefcase,
    BookOpen,
    Activity,
    Group,
    LineChart,
    UserCheck,
    PieChart,
    PlusCircle,
    Save,
    DollarSign,
    Landmark,
    ArrowLeftRight,
    Target,
    Filter,
    Network,
    Rocket,
    Calendar,
    Star,
    BarChart,
    History,
    List,
    MessagesSquare,
    FileCode,
    Cog,
    UserCircle,
    Folder,
    FolderKanban,
    NotebookText,
    PenTool,
    Table,
    Layers,
    Split,
    ShoppingCart,
    ChartSpline,
} from 'lucide-react';

export interface MenuItem {
    id: AdvisorMenuId | ClientMenuId;
    name: string;
    route: AdvisorMenuRoute | ClientMenuRoute | '';
    icon?: string | React.ReactElement;
    iconComponent?: React.ReactElement;
    parent?: AdvisorMenuId | ClientMenuId;
    children?: Array<AdvisorMenuId | ClientMenuId>;
    hidden?: boolean;
}

export const brokerMenu: MenuItem[] = [
    {
        id: AdvisorMenuId.Dashboard,
        name: 'Dashboard',
        route: AdvisorMenuRoute.Dashboard,
        icon: 'fas fa-fw fa-th-large',
    },
    {
        id: AdvisorMenuId.Accounts,
        name: 'Accounts',
        icon: 'fas fa-fw fa-user-friends',
        route: '',
        children: [
            AdvisorMenuId.AccountsBalances,
            AdvisorMenuId.AccountsPositions,
            AdvisorMenuId.AccountsRegister,
            AdvisorMenuId.AccountsActivity,
        ],
    },
    {
        id: AdvisorMenuId.AccountsBalances,
        name: 'Valuation',
        route: AdvisorMenuRoute.AccountsValuation,
        parent: AdvisorMenuId.Accounts,
    },
    {
        id: AdvisorMenuId.AccountsPositions,
        name: 'Positions',
        route: AdvisorMenuRoute.AccountsPositions,
        parent: AdvisorMenuId.Accounts,
    },
    {
        id: AdvisorMenuId.AccountsRegister,
        name: 'Account Register',
        route: AdvisorMenuRoute.AccountsRegister,
        parent: AdvisorMenuId.Accounts,
    },
    {
        id: AdvisorMenuId.AccountsActivity,
        name: 'Latest Activity',
        route: AdvisorMenuRoute.AccountsActivity,
        parent: AdvisorMenuId.Accounts,
    },
    {
        id: AdvisorMenuId.Trading,
        name: 'Trading',
        route: '',
        icon: 'fas fa-fw fa-hand-holding-usd',
        children: [AdvisorMenuId.TradingEquity, AdvisorMenuId.TradingAccountOrModel, AdvisorMenuId.TradingTradeBlotter],
    },
    {
        id: AdvisorMenuId.TradingEquity,
        name: 'Equity',
        route: AdvisorMenuRoute.TradingEquity,
        parent: AdvisorMenuId.Trading,
    },
    {
        id: AdvisorMenuId.TradingAccountOrModel,
        name: 'Account or Model',
        route: AdvisorMenuRoute.TradingAccountOrModel,
        parent: AdvisorMenuId.Trading,
    },
    {
        id: AdvisorMenuId.TradingTradeBlotter,
        name: 'Saved Orders',
        route: AdvisorMenuRoute.TradingTradeBlotter,
        parent: AdvisorMenuId.Trading,
    },
    {
        id: AdvisorMenuId.Transfers,
        name: 'Transfers',
        route: '',
        icon: 'fas fa-fw fa-dollar-sign',
        children: [AdvisorMenuId.TransfersMoney, AdvisorMenuId.TransfersAcats, AdvisorMenuId.TransfersBankLinks],
    },
    {
        id: AdvisorMenuId.TransfersMoney,
        name: isIndia() ? 'History' : 'Money',
        route: AdvisorMenuRoute.TransfersMoney,
        parent: AdvisorMenuId.Transfers,
    },
    {
        id: AdvisorMenuId.TransfersAcats,
        name: 'ACATs',
        route: AdvisorMenuRoute.TransfersAcats,
        parent: AdvisorMenuId.Transfers,
        hidden: isAxos(),
    },
    {
        id: AdvisorMenuId.TransfersBankLinks,
        name: isIndia() ? 'Manage Funds' : 'Bank Links',
        route: AdvisorMenuRoute.TransfersBankLinks,
        parent: AdvisorMenuId.Transfers,
    },
    {
        id: AdvisorMenuId.Clients,
        name: 'Clients',
        icon: 'fas fa-fw fa-users',
        route: AdvisorMenuRoute.ClientsAllClients,
    },
    {
        id: AdvisorMenuId.ClientsAllClients,
        name: 'All Clients',
        route: AdvisorMenuRoute.ClientsAllClients,
        parent: AdvisorMenuId.Clients,
    },
    {
        id: AdvisorMenuId.ClientsAddNewClient,
        name: 'Add New Client',
        route: '',
        parent: AdvisorMenuId.Clients,
    },
    {
        id: AdvisorMenuId.Insights,
        name: 'Insights',
        icon: 'fas fa-fw fa-lightbulb',
        route: '',
        children: [
            AdvisorMenuId.InsightsHome,
            AdvisorMenuId.InsightsOptionRadar,
            AdvisorMenuId.InsightsEtfCenter,
            AdvisorMenuId.InsightsScreener,
            AdvisorMenuId.InsightsSectors,
            AdvisorMenuId.InsightsIpoCenter,
            AdvisorMenuId.InsightsEarnings,
            AdvisorMenuId.InsightsAnalystRatings,
        ],
    },
    {
        id: AdvisorMenuId.InsightsHome,
        name: 'Market Overview',
        route: AdvisorMenuRoute.Insights,
        parent: AdvisorMenuId.Insights,
    },
    {
        id: AdvisorMenuId.InsightsOptionRadar,
        name: 'Options Radar',
        route: AdvisorMenuRoute.InsightsOptionRadar,
        parent: AdvisorMenuId.Insights,
    },
    {
        id: AdvisorMenuId.InsightsEtfCenter,
        name: 'ETF Center',
        route: AdvisorMenuRoute.InsightsEtfCenter,
        parent: AdvisorMenuId.Insights,
    },
    {
        id: AdvisorMenuId.InsightsSectors,
        route: AdvisorMenuRoute.InsightsSectors,
        name: 'Sectors & Industries',
        parent: AdvisorMenuId.Insights,
    },
    {
        id: AdvisorMenuId.InsightsIpoCenter,
        route: AdvisorMenuRoute.InsightsIpo,
        name: 'IPO Center',
        parent: AdvisorMenuId.Insights,
    },
    {
        id: AdvisorMenuId.InsightsEarnings,
        route: AdvisorMenuRoute.InsightsEarnings,
        name: 'Earnings Calendar',
        parent: AdvisorMenuId.Insights,
    },
    {
        id: AdvisorMenuId.InsightsScreener,
        route: AdvisorMenuRoute.InsightsScreener,
        name: 'Screeners',
        parent: AdvisorMenuId.Insights,
    },
    {
        id: AdvisorMenuId.InsightsAnalystRatings,
        route: AdvisorMenuRoute.InsightsAnalystRatings,
        name: 'Analyst Rating',
        parent: AdvisorMenuId.Insights,
        hidden: !isAdvisor(),
    },
    {
        id: AdvisorMenuId.Reports,
        name: 'Reports',
        route: '',
        icon: 'fas fa-fw fa-file-invoice',
        children: [
            AdvisorMenuId.ReportsAUMByAccount,
            AdvisorMenuId.ReportsAUMByAsset,
            AdvisorMenuId.ReportsAccountActivity,
            AdvisorMenuId.ReportsAccountHistory,
            AdvisorMenuId.ReportsPositionsByAccount,
            AdvisorMenuId.ReportsClientList,
        ],
    },
    {
        id: AdvisorMenuId.ReportsAUMByAccount,
        name: 'AUM by Account',
        route: AdvisorMenuRoute.ReportsAumByAccount,
        parent: AdvisorMenuId.Reports,
    },
    {
        id: AdvisorMenuId.ReportsAUMByAsset,
        name: 'AUM by Asset',
        route: AdvisorMenuRoute.ReportsAumByAsset,
        parent: AdvisorMenuId.Reports,
    },
    {
        id: AdvisorMenuId.ReportsAccountActivity,
        name: 'Trading Activity',
        route: AdvisorMenuRoute.ReportsAccountActivity,
        parent: AdvisorMenuId.Reports,
    },
    {
        id: AdvisorMenuId.ReportsAccountHistory,
        name: 'Account History',
        route: AdvisorMenuRoute.ReportsAccountHistory,
        parent: AdvisorMenuId.Reports,
    },
    {
        id: AdvisorMenuId.ReportsPositionsByAccount,
        name: 'Positions by Account',
        route: AdvisorMenuRoute.ReportsPositionsByAccount,
        parent: AdvisorMenuId.Reports,
    },
    {
        id: AdvisorMenuId.ReportsClientList,
        name: 'Client List',
        route: AdvisorMenuRoute.ReportsClientList,
        parent: AdvisorMenuId.Reports,
    },
    {
        id: AdvisorMenuId.Support,
        name: 'Support',
        route: '',
        icon: 'fas fa-fw fa-question-circle',
        hidden: !isAxos(),
        children: [AdvisorMenuId.ContactSupport, AdvisorMenuId.FormLibrary],
    },
    {
        id: AdvisorMenuId.ContactSupport,
        name: 'Contact Support',
        route: AdvisorMenuRoute.ContactSupport,
        parent: AdvisorMenuId.Support,
        hidden: !isAxos(),
    },
    {
        id: AdvisorMenuId.FormLibrary,
        name: 'Form Library',
        route: AdvisorMenuRoute.FormLibrary,
        parent: AdvisorMenuId.Support,
        hidden: !isAxos(),
    },
    {
        id: AdvisorMenuId.Tools,
        name: 'Tools',
        route: '',
        icon: 'fas fa-fw fa-tools',
        children: [AdvisorMenuId.ToolsAlphaTools, AdvisorMenuId.ToolsOnboarding],
    },
    {
        id: AdvisorMenuId.ToolsAlphaTools,
        name: 'AlphaTools',
        route: AdvisorMenuRoute.ToolsAlphatools,
        parent: AdvisorMenuId.Tools,
    },
    {
        id: AdvisorMenuId.ToolsOnboarding,
        name: 'Onboarding',
        route: AdvisorMenuRoute.ToolsOnboarding,
        icon: <OnboardingIcon />,
        parent: AdvisorMenuId.Tools,
    },
    {
        id: AdvisorMenuId.DocumentsStatements,
        name: 'Statements',
        route: AdvisorMenuRoute.DocumentsStatements,
        parent: AdvisorMenuId.Documents,
    },
    {
        id: AdvisorMenuId.DocumentsConfirmations,
        name: 'Confirmations',
        route: AdvisorMenuRoute.DocumentsConfirmations,
        parent: AdvisorMenuId.Documents,
    },
    {
        id: AdvisorMenuId.DocumentsTaxDocuments,
        name: 'Tax Documents',
        route: AdvisorMenuRoute.DocumentsTaxDocuments,
        parent: AdvisorMenuId.Documents,
    },
    {
        id: AdvisorMenuId.Settings,
        name: 'Settings',
        route: '',
        icon: 'fas fa-fw fa-cogs',
        children: [AdvisorMenuId.SettingsYourProfile, AdvisorMenuId.SettingsPlatformSettings],
    },
    {
        id: AdvisorMenuId.SettingsYourProfile,
        name: 'Your Profile',
        route: AdvisorMenuRoute.SettingsYourProfile,
        parent: AdvisorMenuId.Settings,
    },
    {
        id: AdvisorMenuId.SettingsPlatformSettings,
        name: 'Platform Settings',
        route: AdvisorMenuRoute.SettingsPlatformSettings,
        parent: AdvisorMenuId.Settings,
    },
];

export const advisorMenu = (hasInventoryAccount: boolean): MenuItem[] => {
    return [
        {
            id: AdvisorMenuId.Dashboard,
            name: 'Dashboard',
            route: AdvisorMenuRoute.Dashboard,
            iconComponent: <Home />,
        },
        {
            id: AdvisorMenuId.Accounts,
            name: 'Accounts',
            iconComponent: <Users />,
            route: '',
            children: [
                AdvisorMenuId.AccountsBalances,
                AdvisorMenuId.AccountsPositions,
                AdvisorMenuId.AccountsRegister,
                AdvisorMenuId.AccountsGroups,
                AdvisorMenuId.AccountsActivity,
            ],
        },
        {
            id: AdvisorMenuId.AccountsBalances,
            name: 'Valuation',
            iconComponent: <Scale />,
            route: AdvisorMenuRoute.AccountsValuation,
            parent: AdvisorMenuId.Accounts,
        },
        {
            id: AdvisorMenuId.AccountsPositions,
            name: 'Positions',
            iconComponent: <Briefcase />,
            route: AdvisorMenuRoute.AccountsPositions,
            parent: AdvisorMenuId.Accounts,
        },
        {
            id: AdvisorMenuId.AccountsRegister,
            name: 'Account Register',
            iconComponent: <BookOpen />,
            route: AdvisorMenuRoute.AccountsRegister,
            parent: AdvisorMenuId.Accounts,
        },
        {
            id: AdvisorMenuId.AccountsGroups,
            name: 'Groups',
            iconComponent: <Group />,
            route: AdvisorMenuRoute.AccountsGroups,
            parent: AdvisorMenuId.Accounts,
        },
        {
            id: AdvisorMenuId.AccountsActivity,
            name: 'Latest Activity',
            iconComponent: <Activity />,
            route: AdvisorMenuRoute.AccountsActivity,
            parent: AdvisorMenuId.Accounts,
        },
        {
            id: AdvisorMenuId.Models,
            name: 'Models',
            iconComponent: <Library />,
            route: AdvisorMenuRoute.ModelsManageModels,
            hidden: !hasInventoryAccount,
        },
        {
            id: AdvisorMenuId.ModelsDriftReport,
            name: 'Drift Report',
            iconComponent: <LineChart />,
            route: AdvisorMenuRoute.ModelsDriftReport,
            parent: AdvisorMenuId.Models,
            hidden: !hasInventoryAccount,
        },
        {
            id: AdvisorMenuId.Trading,
            name: 'Trading',
            route: '',
            iconComponent: <TrendingUp />,
            children: [
                AdvisorMenuId.TradingEquity,
                AdvisorMenuId.TradingAccountOrModel,
                AdvisorMenuId.TradingBasket,
                AdvisorMenuId.TradingPreAllocation,
                AdvisorMenuId.TradingPostAllocation,
                AdvisorMenuId.TradingAllocation,
                AdvisorMenuId.TradingTradeBlotter,
                AdvisorMenuId.TradingPreIpoMarketplace,
            ],
        },
        {
            id: AdvisorMenuId.TradingEquity,
            name: 'Equity',
            iconComponent: <ChartSpline />,
            route: AdvisorMenuRoute.TradingEquity,
            parent: AdvisorMenuId.Trading,
        },
        {
            id: AdvisorMenuId.TradingAccountOrModel,
            name: `${hasInventoryAccount ? 'Account or Model' : 'Account'}`,
            iconComponent: <UserCheck />,
            route: AdvisorMenuRoute.TradingAccountOrModel,
            parent: AdvisorMenuId.Trading,
        },
        {
            id: AdvisorMenuId.TradingBasket,
            name: 'Basket Trading',
            iconComponent: <ShoppingCart />,
            route: AdvisorMenuRoute.TradingBasket,
            parent: AdvisorMenuId.Trading,
        },
        {
            id: AdvisorMenuId.TradingAllocation,
            name: 'Legacy Allocation',
            iconComponent: <PieChart />,
            route: AdvisorMenuRoute.TradingAllocation,
            parent: AdvisorMenuId.Trading,
            hidden: !hasInventoryAccount,
        },
        {
            id: AdvisorMenuId.TradingPreAllocation,
            name: 'Pre-Allocation',
            iconComponent: <PlusCircle />,
            route: AdvisorMenuRoute.TradingPreAllocation,
            parent: AdvisorMenuId.Trading,
            hidden: !hasInventoryAccount,
        },
        {
            id: AdvisorMenuId.TradingPostAllocation,
            name: 'Post-Allocation',
            iconComponent: <Split />,
            route: AdvisorMenuRoute.TradingPostAllocation,
            parent: AdvisorMenuId.Trading,
            hidden: !hasInventoryAccount,
        },
        {
            id: AdvisorMenuId.TradingTradeBlotter,
            name: 'Saved Orders',
            iconComponent: <Save />,
            route: AdvisorMenuRoute.TradingTradeBlotter,
            parent: AdvisorMenuId.Trading,
        },
        {
            id: AdvisorMenuId.TradingPreIpoMarketplace,
            name: 'SPVs & Pre-IPO Companies',
            iconComponent: <Layers />,
            route: AdvisorMenuRoute.TradingPreIpoMarketplace,
            parent: AdvisorMenuId.Trading,
        },
        {
            id: AdvisorMenuId.Transfers,
            name: 'Transfers',
            route: '',
            iconComponent: <ArrowRightLeft />,
            children: [AdvisorMenuId.TransfersMoney, AdvisorMenuId.TransfersAcats, AdvisorMenuId.TransfersBankLinks],
        },
        {
            id: AdvisorMenuId.TransfersMoney,
            name: 'Money',
            iconComponent: <DollarSign />,
            route: AdvisorMenuRoute.TransfersMoney,
            parent: AdvisorMenuId.Transfers,
        },
        {
            id: AdvisorMenuId.TransfersAcats,
            name: 'ACATs',
            iconComponent: <ArrowLeftRight />,
            route: AdvisorMenuRoute.TransfersAcats,
            parent: AdvisorMenuId.Transfers,
            hidden: isAxos(),
        },
        {
            id: AdvisorMenuId.TransfersBankLinks,
            name: 'Bank Links',
            iconComponent: <Landmark />,
            route: AdvisorMenuRoute.TransfersBankLinks,
            parent: AdvisorMenuId.Transfers,
        },
        {
            id: AdvisorMenuId.Clients,
            name: 'Clients',
            iconComponent: <Users />,
            route: AdvisorMenuRoute.ClientsAllClients,
        },
        {
            id: AdvisorMenuId.ClientsAllClients,
            name: 'All Clients',
            iconComponent: <Users />,
            route: AdvisorMenuRoute.ClientsAllClients,
            parent: AdvisorMenuId.Clients,
        },
        {
            id: AdvisorMenuId.ClientsAddNewClient,
            name: 'Add New Client',
            iconComponent: <PlusCircle />,
            route: '',
            parent: AdvisorMenuId.Clients,
        },
        {
            id: AdvisorMenuId.Insights,
            name: 'Insights: US Markets',
            iconComponent: <Lightbulb />,
            route: '',
            children: [
                AdvisorMenuId.InsightsHome,
                AdvisorMenuId.InsightsOptionRadar,
                AdvisorMenuId.InsightsEtfCenter,
                AdvisorMenuId.InsightsScreener,
                AdvisorMenuId.InsightsSectors,
                AdvisorMenuId.InsightsIpoCenter,
                AdvisorMenuId.InsightsEarnings,
                AdvisorMenuId.InsightsAnalystRatings,
            ],
        },
        {
            id: AdvisorMenuId.InsightsHome,
            name: 'Market Overview',
            iconComponent: <LayoutDashboard />,
            route: AdvisorMenuRoute.Insights,
            parent: AdvisorMenuId.Insights,
        },
        {
            id: AdvisorMenuId.InsightsOptionRadar,
            name: 'Options Radar',
            iconComponent: <Target />,
            route: AdvisorMenuRoute.InsightsOptionRadar,
            parent: AdvisorMenuId.Insights,
        },
        {
            id: AdvisorMenuId.InsightsEtfCenter,
            name: 'ETF Center',
            iconComponent: <Landmark />,
            route: AdvisorMenuRoute.InsightsEtfCenter,
            parent: AdvisorMenuId.Insights,
        },
        {
            id: AdvisorMenuId.InsightsSectors,
            route: AdvisorMenuRoute.InsightsSectors,
            name: 'Sectors & Industries',
            iconComponent: <Network />,
            parent: AdvisorMenuId.Insights,
        },
        {
            id: AdvisorMenuId.InsightsIpoCenter,
            route: AdvisorMenuRoute.InsightsIpo,
            name: 'IPO Center',
            iconComponent: <Rocket />,
            parent: AdvisorMenuId.Insights,
        },
        {
            id: AdvisorMenuId.InsightsEarnings,
            route: AdvisorMenuRoute.InsightsEarnings,
            name: 'Earnings Calendar',
            iconComponent: <Calendar />,
            parent: AdvisorMenuId.Insights,
        },
        {
            id: AdvisorMenuId.InsightsScreener,
            route: AdvisorMenuRoute.InsightsScreener,
            name: 'Screeners',
            iconComponent: <Filter />,
            parent: AdvisorMenuId.Insights,
        },
        {
            id: AdvisorMenuId.InsightsAnalystRatings,
            route: AdvisorMenuRoute.InsightsAnalystRatings,
            name: 'Analyst Rating',
            iconComponent: <Star />,
            parent: AdvisorMenuId.Insights,
            hidden: !isAdvisor(),
        },
        {
            id: AdvisorMenuId.Reports,
            name: 'Reports',
            route: '',
            iconComponent: <FileText />,
            children: [
                AdvisorMenuId.ReportsAUMByAccount,
                AdvisorMenuId.ReportsAUMByAsset,
                AdvisorMenuId.ReportsAccountActivity,
                AdvisorMenuId.ReportsAccountHistory,
                AdvisorMenuId.ReportsPositionsByAccount,
                AdvisorMenuId.ReportsClientList,
            ],
        },
        {
            id: AdvisorMenuId.ReportsAUMByAccount,
            name: 'AUM by Account',
            iconComponent: <BarChart />,
            route: AdvisorMenuRoute.ReportsAumByAccount,
            parent: AdvisorMenuId.Reports,
        },
        {
            id: AdvisorMenuId.ReportsAUMByAsset,
            name: 'AUM by Asset',
            iconComponent: <BarChart />,
            route: AdvisorMenuRoute.ReportsAumByAsset,
            parent: AdvisorMenuId.Reports,
        },
        {
            id: AdvisorMenuId.ReportsAccountActivity,
            name: 'Trading Activity',
            iconComponent: <Activity />,
            route: AdvisorMenuRoute.ReportsAccountActivity,
            parent: AdvisorMenuId.Reports,
        },
        {
            id: AdvisorMenuId.ReportsAccountHistory,
            name: 'Account History',
            iconComponent: <History />,
            route: AdvisorMenuRoute.ReportsAccountHistory,
            parent: AdvisorMenuId.Reports,
        },
        {
            id: AdvisorMenuId.ReportsPositionsByAccount,
            name: 'Positions by Account',
            iconComponent: <Briefcase />,
            route: AdvisorMenuRoute.ReportsPositionsByAccount,
            parent: AdvisorMenuId.Reports,
        },
        {
            id: AdvisorMenuId.ReportsClientList,
            name: 'Client List',
            iconComponent: <List />,
            route: AdvisorMenuRoute.ReportsClientList,
            parent: AdvisorMenuId.Reports,
        },
        {
            id: AdvisorMenuId.Support,
            name: 'Support',
            route: '',
            iconComponent: <HelpCircle />,
            hidden: !isAxos(),
            children: [AdvisorMenuId.ContactSupport, AdvisorMenuId.FormLibrary],
        },
        {
            id: AdvisorMenuId.ContactSupport,
            name: 'Contact Support',
            iconComponent: <MessagesSquare />,
            route: AdvisorMenuRoute.ContactSupport,
            parent: AdvisorMenuId.Support,
            hidden: !isAxos(),
        },
        {
            id: AdvisorMenuId.FormLibrary,
            name: 'Form Library',
            iconComponent: <FileCode />,
            route: AdvisorMenuRoute.FormLibrary,
            parent: AdvisorMenuId.Support,
            hidden: !isAxos(),
        },
        {
            id: AdvisorMenuId.Tools,
            name: 'Tools',
            route: '',
            iconComponent: <Wrench />,
            children: [AdvisorMenuId.ToolsAlphaTools, AdvisorMenuId.ToolsOnboarding],
        },
        {
            id: AdvisorMenuId.ToolsAlphaTools,
            name: 'AlphaTools',
            route: AdvisorMenuRoute.ToolsAlphatools,
            parent: AdvisorMenuId.Tools,
        },
        {
            id: AdvisorMenuId.ToolsOnboarding,
            name: 'Onboarding',
            route: AdvisorMenuRoute.ToolsOnboarding,
            iconComponent: <OnboardingIcon />,
            parent: AdvisorMenuId.Tools,
        },
        // {
        //     id: AdvisorMenuId.DocumentsStatements,
        //     name: 'Statements',
        //     route: AdvisorMenuRoute.DocumentsStatements,
        //     parent: AdvisorMenuId.Documents,
        // },
        // {
        //     id: AdvisorMenuId.DocumentsConfirmations,
        //     name: 'Confirmations',
        //     route: AdvisorMenuRoute.DocumentsConfirmations,
        //     parent: AdvisorMenuId.Documents,
        // },
        // {
        //     id: AdvisorMenuId.DocumentsTaxDocuments,
        //     name: 'Tax Documents',
        //     route: AdvisorMenuRoute.DocumentsTaxDocuments,
        //     parent: AdvisorMenuId.Documents,
        // },
        {
            id: AdvisorMenuId.Settings,
            name: 'Settings',
            route: '',
            iconComponent: <Settings />,
            children: [AdvisorMenuId.SettingsYourProfile, AdvisorMenuId.SettingsPlatformSettings],
        },
        {
            id: AdvisorMenuId.SettingsYourProfile,
            name: 'Your Profile',
            iconComponent: <UserCircle />,
            route: AdvisorMenuRoute.SettingsYourProfile,
            parent: AdvisorMenuId.Settings,
        },
        {
            id: AdvisorMenuId.SettingsPlatformSettings,
            name: 'Platform Settings',
            iconComponent: <Cog />,
            route: AdvisorMenuRoute.SettingsPlatformSettings,
            parent: AdvisorMenuId.Settings,
        },
        {
            id: AdvisorMenuId.Components,
            name: 'Components',
            route: '',
            iconComponent: <PenTool />,
            children: [AdvisorMenuId.ComponentsTable],
            hidden: !import.meta.env.DEV,
        },
        {
            id: AdvisorMenuId.ComponentsTable,
            name: 'Table',
            route: AdvisorMenuRoute.ComponentsTable,
            parent: AdvisorMenuId.Components,
            iconComponent: <Table />,
            hidden: !import.meta.env.DEV,
        },
    ];
};

export const clientMenu: MenuItem[] = [
    {
        id: ClientMenuId.Dashboard,
        name: 'Dashboard',
        route: ClientMenuRoute.Dashboard,
        iconComponent: <LayoutDashboard />,
    },
    {
        id: ClientMenuId.Account,
        name: 'Account',
        iconComponent: <Users />,
        route: '',
        children: [ClientMenuId.AccountSummary, ClientMenuId.AccountHistory],
    },
    {
        id: ClientMenuId.AccountSummary,
        name: 'Account Summary',
        iconComponent: <NotebookText />,
        route: ClientMenuRoute.AccountSummary,
        parent: ClientMenuId.Account,
    },
    {
        id: ClientMenuId.AccountHistory,
        name: 'Account History',
        iconComponent: <History />,
        route: ClientMenuRoute.AccountHistory,
        parent: ClientMenuId.Account,
    },
    {
        id: ClientMenuId.Position,
        name: 'Positions',
        route: ClientMenuRoute.Positions,
        iconComponent: <PieChart />,
    },
    {
        id: ClientMenuId.Activity,
        name: 'Latest Activity',
        route: ClientMenuRoute.Activity,
        iconComponent: <Activity />,
    },
    {
        id: ClientMenuId.Transfers,
        name: 'Transfers',
        route: '',
        iconComponent: <ArrowRightLeft />,
        children: [ClientMenuId.TransfersMoney, ClientMenuId.TransfersBankLinks, ClientMenuId.TransfersAcats],
    },
    {
        id: ClientMenuId.TransfersAcats,
        name: 'ACATs',
        route: ClientMenuRoute.Acats,
        iconComponent: <ArrowLeftRight />,
        parent: ClientMenuId.Transfers,
        hidden: isAxos(),
    },
    {
        id: ClientMenuId.TransfersMoney,
        name: isIndia() ? 'History' : 'Money',
        route: ClientMenuRoute.Money,
        parent: ClientMenuId.Transfers,
    },
    {
        id: ClientMenuId.TransfersBankLinks,
        name: isIndia() ? 'Manage Funds' : 'Bank Links',
        route: ClientMenuRoute.BankLinks,
        parent: ClientMenuId.Transfers,
    },
    {
        id: ClientMenuId.AccountDocuments,
        name: 'Account Documents',
        route: ClientMenuRoute.AccountDocuments,
        iconComponent: <Folder />,
    },
    {
        id: ClientMenuId.ClientDocuments,
        name: 'Client Documents',
        route: ClientMenuRoute.ClientDocuments,
        iconComponent: <FolderKanban />,
    },
    {
        id: ClientMenuId.Settings,
        name: 'Settings',
        route: '',
        iconComponent: <Settings />,
        children: [ClientMenuId.SettingsProfile, ClientMenuId.SettingsPlatformSettings],
    },
    {
        id: ClientMenuId.SettingsProfile,
        name: 'Profile',
        iconComponent: <UserCircle />,
        route: ClientMenuRoute.SettingsYourProfile,
        parent: ClientMenuId.Settings,
    },
    {
        id: ClientMenuId.SettingsPlatformSettings,
        name: 'Platform Settings',
        route: ClientMenuRoute.SettingsPlatformSettings,
        parent: ClientMenuId.Settings,
    },
];
